/**
 * **Links** — a column or a condition that belongs to another screen, shown on
 * this one (`docs/plans/screen-links.md`, Part 2).
 *
 *     stored     {field, link: {screen, field}, width?, hide?, pinned?}
 *                {id, name, link: {screen, id}}
 *     resolved   the source screen's column / condition, as it presents it
 *                to this reader, with the member's view on top
 *
 * **Two forms, and only one is ever stored.** The stored form is the link and
 * the member's view of it; the materialised form is what the grid and the
 * builder draw. `materialiseLinks` goes one way on the way in, `stripLinks` and
 * `stripLinkedConditions` the other on the way out — so a group that changes a
 * column's format reaches every member at once, and no copy of it is left to go
 * stale in anybody's workspace.
 *
 * **A linked condition is evaluated by reference, always.** Its `{screen, id}`
 * goes to the server in the `sealedConditions` request whether or not the
 * source seals its library for this reader, so no rule is ever combined into
 * `filter` — and so none can be saved with it.
 *
 * Pure, and the island's, for `copy-from-screen.ts`'s reason: the host injects
 * the screens and the loader; this knows no URL.
 */

import type {
  SavedScreenerFilter,
  SavedScreenerFilterLink,
  ScreenerColumn,
  ScreenerColumnLink,
  ScreenerConfig,
} from "@qufolio/model";

import type { CopyFromScreen, CopySourceDefinition, CopySourceScreen } from "./copy-from-screen.js";
import { isSealed } from "./saved-filters.js";

export type LinkedColumn = ScreenerColumn & { link: ScreenerColumnLink };
export type LinkedCondition = SavedScreenerFilter & { link: SavedScreenerFilterLink };

/**
 * The host's half: the screens a member may link from — screens of groups they
 * hold, which the picker lists — the loader for one, and what resolution found.
 * Offered to **every** member (decision 4), unlike Copy from screen….
 */
export interface ScreenLinks extends CopyFromScreen {
  /** A source screen as a reader names it — `{ownerName: "JP", name: "Persons"}`. */
  describe: (key: string) => Pick<CopySourceScreen, "ownerName" | "name"> | undefined;
  /** The linked entries that resolved to nothing, by `columnLinkKey` / `conditionLinkKey`. */
  missing: ReadonlySet<string>;
}

/** What a member owns of a linked column; everything else is the source's. */
export const LINK_VIEW_FIELDS = ["width", "hide", "pinned"] as const;

export function isLinkedColumn(column: ScreenerColumn | undefined): column is LinkedColumn {
  return !!column?.link;
}

export function isLinkedCondition(filter: SavedScreenerFilter | undefined): filter is LinkedCondition {
  return !!filter?.link;
}

/** The key a placeholder is reported under in `ScreenLinks.missing`. */
export const columnLinkKey = (column: Pick<ScreenerColumn, "field">) => `col:${column.field}`;
export const conditionLinkKey = (filter: Pick<SavedScreenerFilter, "id">) => `cond:${filter.id}`;

/** `JP · Persons` — how a 🔗 names its source. */
export function linkLabel(links: Pick<ScreenLinks, "describe"> | undefined, screen: string): string {
  const source = links?.describe(screen);
  return source ? `${source.ownerName} · ${source.name}` : screen;
}

/** Every source screen this config links from, once each, sorted. */
export function linkedScreens(
  config: Pick<ScreenerConfig, "columns" | "savedFilters">,
): string[] {
  const keys = new Set<string>();
  for (const c of config.columns ?? []) if (c.link) keys.add(c.link.screen);
  for (const f of config.savedFilters ?? []) if (f.link) keys.add(f.link.screen);
  return [...keys].sort();
}

// ── the stored form ─────────────────────────────────────────────────────────

/** A linked column as stored: its field, the link, the member's view. */
export function columnStub(column: LinkedColumn): LinkedColumn {
  return {
    field: column.field,
    link: { ...column.link },
    ...(column.width !== undefined ? { width: column.width } : {}),
    ...(column.hide !== undefined ? { hide: column.hide } : {}),
    ...(column.pinned !== undefined ? { pinned: column.pinned } : {}),
  };
}

/** A linked condition as stored: id, last known name, the link — never a rule. */
export function conditionStub(filter: LinkedCondition): LinkedCondition {
  return { id: filter.id, name: filter.name, link: { ...filter.link } };
}

/**
 * Columns with every linked one reduced to what is stored. The same array, by
 * identity, when none is linked — the common case costs a patch nothing.
 */
export function stripLinks<T extends readonly ScreenerColumn[]>(columns: T): T {
  if (!columns.some(isLinkedColumn)) return columns;
  return columns.map((c) => (isLinkedColumn(c) ? columnStub(c) : c)) as unknown as T;
}

/** A library with every linked condition reduced to what is stored. Identity when none is. */
export function stripLinkedConditions<T extends readonly SavedScreenerFilter[]>(filters: T): T {
  if (!filters.some(isLinkedCondition)) return filters;
  return filters.map((f) => (isLinkedCondition(f) ? conditionStub(f) : f)) as unknown as T;
}

/**
 * A library as it goes into **another screen of the member's** — Duplicate,
 * New screen: the open conditions whole, the linked ones as links (they carry
 * no rule, so they travel), and the screen's own sealed ones left behind (each
 * names a rule only its own screen has; `withoutSealed`'s reason).
 */
export function libraryForAnotherScreen(filters: readonly SavedScreenerFilter[]): SavedScreenerFilter[] {
  return filters
    .filter((f) => isLinkedCondition(f) || !isSealed(f))
    .map((f) => (isLinkedCondition(f) ? conditionStub(f) : f));
}

// ── the materialised form ──────────────────────────────────────────────────

/**
 * One source screen as resolution has it: `undefined` while it is loading,
 * `null` when it cannot be read (gone, unpublished, no longer yours).
 */
export type ResolveSource = (screen: string) => CopySourceDefinition | null | undefined;

export interface Materialised {
  columns: ScreenerColumn[] | undefined;
  savedFilters: SavedScreenerFilter[] | undefined;
  /** Placeholders, by `columnLinkKey` / `conditionLinkKey`. */
  missing: Set<string>;
}

/**
 * The config's links, resolved: each linked column dressed in the source's
 * definition and the member's view, each linked condition as the source
 * presents it to this reader.
 *
 * - **Loading** leaves the stub as it is — its field still draws, with the
 *   server's label — and reports nothing missing: a placeholder that flashed
 *   on every page load would teach people to ignore it.
 * - **Missing** keeps the stub, and its column header says so,
 *   `PPS Trend — no longer in Persons`, from `labelOf`; a condition keeps its
 *   last known name and the picker says the rest.
 */
export function materialiseLinks(
  config: Pick<ScreenerConfig, "columns" | "savedFilters">,
  resolve: ResolveSource,
  options: {
    /** The source's name, for a placeholder — `Persons`. */
    screenName?: (screen: string) => string | undefined;
    /** The server's label for a field, for a placeholder header. */
    labelOf?: (field: string) => string | undefined;
  } = {},
): Materialised {
  const missing = new Set<string>();
  const nameOf = (screen: string) => options.screenName?.(screen) ?? screen;

  const columns = config.columns?.map((column): ScreenerColumn => {
    if (!isLinkedColumn(column)) return column;
    const stub = columnStub(column);
    const source = resolve(column.link.screen);
    if (source === undefined) return stub;
    const found = source?.columns?.find((c) => c.field === column.link.field);
    if (!found) {
      missing.add(columnLinkKey(column));
      const label = options.labelOf?.(column.field) || column.field;
      return { ...stub, header: `${label} — no longer in ${nameOf(column.link.screen)}` };
    }
    // The source's definition without its own view — the member's replaces it
    // — and without a link of its own: a link names one screen, not a chain.
    const { width: _w, hide: _h, pinned: _p, link: _l, ...definition } = found;
    return { ...definition, ...stub };
  });

  const savedFilters = config.savedFilters?.map((filter): SavedScreenerFilter => {
    if (!isLinkedCondition(filter)) return filter;
    const stub = conditionStub(filter);
    const source = resolve(filter.link.screen);
    if (source === undefined) return { ...stub, sealed: true };
    const found = source?.savedFilters?.find((f) => f.id === filter.link.id);
    if (!found) {
      missing.add(conditionLinkKey(filter));
      return { ...stub, sealed: true };
    }
    return {
      id: filter.id,
      name: found.name,
      ...(found.category ? { category: found.category } : {}),
      link: stub.link,
      // As the source presents it to this reader: sealed, or open with its rule
      // for the tooltip. Either way it is evaluated by reference.
      ...(isSealed(found) || !found.filter ? { sealed: true } : { filter: found.filter }),
    };
  });

  return { columns, savedFilters, missing };
}

// ── linking, unlinking, removing ───────────────────────────────────────────

/**
 * The columns with the picked ones appended **as links**, and the fields
 * skipped because this screen already has a column of that field —
 * `copyColumns`' rule, for its reason: one field is one grid column.
 */
export function linkColumns(
  current: readonly ScreenerColumn[],
  picked: readonly ScreenerColumn[],
  screen: string,
): { columns: ScreenerColumn[]; skipped: string[] } {
  const have = new Set(current.map((c) => c.field));
  const skipped: string[] = [];
  const added: ScreenerColumn[] = [];
  for (const column of picked) {
    if (have.has(column.field)) {
      skipped.push(column.field);
      continue;
    }
    have.add(column.field);
    added.push({ field: column.field, link: { screen, field: column.field } });
  }
  return { columns: [...current, ...added], skipped };
}

/**
 * The library with the picked conditions appended as links. Present means the
 * same id — linked or copied before — or the same name, `copyConditions`' rule:
 * two rows reading *JP bull trend* cannot be told apart. The source's id is
 * kept, so a tick names the same condition on both screens.
 */
export function linkConditions(
  current: readonly SavedScreenerFilter[],
  picked: readonly SavedScreenerFilter[],
  screen: string,
): { savedFilters: SavedScreenerFilter[]; skipped: string[] } {
  const ids = new Set(current.flatMap((f) => [f.id, ...(f.link ? [f.link.id] : [])]));
  const names = new Set(current.map((f) => f.name.trim()));
  const skipped: string[] = [];
  const added: SavedScreenerFilter[] = [];
  for (const filter of picked) {
    if (ids.has(filter.id) || names.has(filter.name.trim())) {
      skipped.push(filter.name);
      continue;
    }
    ids.add(filter.id);
    names.add(filter.name.trim());
    added.push({ id: filter.id, name: filter.name, link: { screen, id: filter.id } });
  }
  return { savedFilters: [...current, ...added], skipped };
}

/**
 * **Unlink**: the materialised column becomes the member's own — field and
 * formatting, nothing secret (the columns are server fields; the logic is in
 * the conditions). Conditions have no counterpart: unlinking one would copy a
 * rule the reader may not hold (decision 3).
 */
export function unlinkColumn(columns: readonly ScreenerColumn[], field: string): ScreenerColumn[] {
  return columns.map((c) => {
    if (c.field !== field || !isLinkedColumn(c)) return c;
    const { link: _dropped, ...own } = c;
    return own;
  });
}
